import { quoteIfNeeded } from '@gonfalon/dataviz-experimental/QueryBuilder';
import type { Operator, ParsedToken } from '@gonfalon/dataviz-experimental/QueryBuilder';
import type { ContextAttributeFilter, EventPropertyFilter, GroupFilter, MetricFilter } from '@gonfalon/metrics/types';

// Converts between the filter bar and the form's stored `filters` value, both ways:
//   queryToEventFilter:  bar tokens      -> MetricFilter   (when the user edits)
//   eventFilterToQuery:  MetricFilter    -> query string   (to pre-fill the bar)
// canEditWithQueryBuilder says whether a stored filter can use the bar at all.

type FilterParsedToken = Extract<ParsedToken, { type: 'FILTER' }>;

// Maps raw suggestion prefixes to the stored filter; bare + `ctx.` legacy forms still parse.
const DATA_JSON_PREFIX = 'data_json.';
const CONTEXT_ATTRS_PREFIX = 'context_attributes_json.';

// Synthetic scope-only field: picking kinds here narrows the offered context attributes. Never
// persisted (filterTokenToLeaf drops it) — mirrored from MetricEventQueryBuilder.
const CONTEXT_KIND_FIELD = 'context';

// Filter-bar operator -> metric `op` + `negate`. Comparison ops are single-value/non-negatable (see
// SINGLE_VALUE_OPS); returns null for an op the bar can't map.
function mapBarOp(operator: Operator): { op: string; negate: boolean } | null {
  switch (operator) {
    case '=':
      return { op: 'in', negate: false };
    case '!=':
      return { op: 'in', negate: true };
    case '//':
      return { op: 'matches', negate: false };
    case '!//':
      return { op: 'matches', negate: true };
    case '**':
      return { op: 'contains', negate: false };
    case '^=':
      return { op: 'startsWith', negate: false };
    case '=^':
      return { op: 'endsWith', negate: false };
    case '>':
      return { op: 'greaterThan', negate: false };
    case '<':
      return { op: 'lessThan', negate: false };
    case '>=':
      return { op: 'greaterThanOrEqual', negate: false };
    case '<=':
      return { op: 'lessThanOrEqual', negate: false };
    default:
      return null;
  }
}

// Backend single-value, non-negatable ops (subset of Go `singleValueOperators`). Guarded in both
// conversion directions or the backend 400s.
const SINGLE_VALUE_OPS = new Set(['greaterThan', 'greaterThanOrEqual', 'lessThan', 'lessThanOrEqual']);

// One filter token -> a leaf (context attribute if prefixed, else event property); null = drop.
function filterTokenToLeaf(token: FilterParsedToken): ContextAttributeFilter | EventPropertyFilter | null {
  // Scope-only affordance (Option A): the `context` token narrows the offered attributes but is never
  // persisted — the real filters are the context_attributes_json.<kind>.<attr> clauses. Drop it.
  if (token.field === 'context') {
    return null;
  }

  const mapped = mapBarOp(token.operator);
  if (!mapped) {
    return null;
  }

  // A filter can be negated two ways: a `!` in front of the field, or a negating operator like `!=`.
  // If both are present they cancel out, so combine them with XOR.
  const negate = mapped.negate !== Boolean(token.negated);
  const values: Array<string | number | boolean> = Array.isArray(token.value) ? token.value : [token.value];

  // Single-value ops (numeric comparison) accept exactly one value and can't be negated; a token that
  // violates that would 400 on save, so drop it (the caller skips a null leaf).
  if (SINGLE_VALUE_OPS.has(mapped.op) && (values.length !== 1 || negate)) {
    return null;
  }

  // Context attribute: first segment after the prefix is the kind, the rest (may contain dots) the attr.
  const field = token.field;
  let contextRest: string | null = null;
  if (field.startsWith(CONTEXT_ATTRS_PREFIX)) {
    contextRest = field.slice(CONTEXT_ATTRS_PREFIX.length);
  } else if (field.startsWith('ctx.')) {
    contextRest = field.slice('ctx.'.length);
  }
  if (contextRest !== null) {
    const parts = contextRest.split('.');
    const contextKind = parts[0] ?? '';
    const attribute = parts.slice(1).join('.');
    // A context attribute needs both a kind and an attribute; a single-segment field
    // (`context_attributes_json.<kind>`) has no attribute and can't be saved, so drop it.
    if (!contextKind || !attribute) {
      return null;
    }
    return {
      type: 'contextAttribute',
      op: mapped.op,
      contextKind,
      attribute,
      values,
      negate,
    };
  }

  // Event property: strip `data_json.` (or legacy `evt.`); a bare field keeps its whole name.
  let attribute = field;
  if (field.startsWith(DATA_JSON_PREFIX)) {
    attribute = field.slice(DATA_JSON_PREFIX.length);
  } else if (field.startsWith('evt.')) {
    attribute = field.slice('evt.'.length);
  }
  return {
    type: 'eventProperty',
    op: mapped.op,
    attribute,
    values,
    negate,
  };
}

// Builds the MetricFilter tree with AND binding tighter than OR: split at each top-level OR into
// segments, AND-join within each, OR-join the segments. A lone filter is returned unwrapped.
function tokensToFilter(tokens: ParsedToken[]): MetricFilter | undefined {
  // OR-separated segments; AND/free-text tokens stay within the current segment.
  const orSegments: MetricFilter[][] = [[]];
  for (const token of tokens) {
    if (token.type === 'FILTER') {
      const leaf = filterTokenToLeaf(token);
      if (leaf) {
        orSegments[orSegments.length - 1].push(leaf);
      }
    } else if (token.type === 'EXPRESSION') {
      const group = tokensToFilter(token.inner);
      if (group) {
        orSegments[orSegments.length - 1].push(group);
      }
    } else if (token.type === 'LOGIC_BOOL' && token.value === 'OR') {
      orSegments.push([]);
    }
    // Plain text and `AND` tokens add nothing, so skip them.
  }

  // Collapse each segment to a leaf (1 filter) or an `and` group (>1); drop empty segments.
  const andGroups = orSegments
    .map((segment): MetricFilter | undefined => {
      if (segment.length === 0) {
        return undefined;
      }
      if (segment.length === 1) {
        return segment[0];
      }
      return { type: 'group', op: 'and', values: segment, negate: false } satisfies GroupFilter;
    })
    .filter((group): group is MetricFilter => group !== undefined);

  if (andGroups.length === 0) {
    return undefined;
  }
  if (andGroups.length === 1) {
    return andGroups[0];
  }

  return { type: 'group', op: 'or', values: andGroups, negate: false } satisfies GroupFilter;
}

// Converts the bar's tokens into the form's `filters` value. An empty query returns undefined, which
// clears the field.
export function queryToEventFilter(tokens: ParsedToken[]): MetricFilter | undefined {
  return tokensToFilter(tokens);
}

// Reverse of mapBarOp: metric op -> bar operator. `negatedOperator` exists where a distinct negated
// token does; others negate via a `!` field prefix.
const OP_TO_UI: Record<string, { operator: Operator; negatedOperator?: Operator }> = {
  in: { operator: '=', negatedOperator: '!=' },
  matches: { operator: '//', negatedOperator: '!//' },
  contains: { operator: '**' },
  startsWith: { operator: '^=' },
  endsWith: { operator: '=^' },
  greaterThan: { operator: '>' },
  greaterThanOrEqual: { operator: '>=' },
  lessThan: { operator: '<' },
  lessThanOrEqual: { operator: '<=' },
};

// Raw prefixed field name matching the bar's field options (`data_json.*` /
// `context_attributes_json.<kind>.*`), so re-parsing lines up.
function rawFieldName(filter: ContextAttributeFilter | EventPropertyFilter): string {
  return filter.type === 'eventProperty'
    ? `${DATA_JSON_PREFIX}${filter.attribute ?? ''}`
    : `${CONTEXT_ATTRS_PREFIX}${filter.contextKind ?? ''}.${filter.attribute ?? ''}`;
}

function renderLeaf(filter: ContextAttributeFilter | EventPropertyFilter): string | null {
  const mapping = OP_TO_UI[filter.op];
  if (!mapping) {
    return null;
  }

  // A single-value op with the wrong value count or a negation can't be shown in the bar without
  // misrepresenting it; drop it so the caller falls back to the legacy editor (canEdit agrees).
  if (SINGLE_VALUE_OPS.has(filter.op) && ((filter.values ?? []).length !== 1 || filter.negate)) {
    return null;
  }

  // Emit raw prefixed forms so a re-loaded filter matches the field options.
  const field = rawFieldName(filter);

  // Negation uses the negated operator (`!=`, `!//`) when there is one, otherwise a `!` prefix.
  let prefix = '';
  let operator = mapping.operator;
  if (filter.negate) {
    if (mapping.negatedOperator) {
      operator = mapping.negatedOperator;
    } else {
      prefix = '!';
    }
  }

  // Quote values the same way the bar does so the string parses back cleanly
  // (handles spaces, commas, colons).
  const values = (filter.values ?? []).map((value) => quoteIfNeeded(String(value))).join(',');
  return `${prefix}${quoteIfNeeded(field)}:${operator}:${values}`;
}

function renderNode(filter: MetricFilter): string | null {
  if (filter.type !== 'group') {
    return renderLeaf(filter);
  }

  const parts = filter.values
    .map((child) => {
      const rendered = renderNode(child);
      if (rendered === null) {
        return null;
      }
      // Wrap nested groups in parentheses so the AND/OR grouping is preserved.
      return child.type === 'group' ? `(${rendered})` : rendered;
    })
    .filter((part): part is string => part !== null);

  if (parts.length === 0) {
    return null;
  }
  if (parts.length === 1) {
    return parts[0];
  }
  return parts.join(filter.op === 'or' ? ' OR ' : ' AND ');
}

// Distinct context kinds across the filter's contextAttribute leaves (first-seen, deduped), used to
// re-emit the scope-only `context` token.
function contextKindsFromFilter(filter: MetricFilter): string[] {
  const kinds: string[] = [];
  const seen = new Set<string>();
  const walk = (node: MetricFilter) => {
    if (node.type === 'group') {
      node.values.forEach(walk);
      return;
    }
    if (node.type === 'contextAttribute' && node.contextKind && !seen.has(node.contextKind)) {
      seen.add(node.contextKind);
      kinds.push(node.contextKind);
    }
  };
  walk(filter);
  return kinds;
}

// Converts a stored MetricFilter back into a query string to pre-fill the bar.
// Returns undefined if none of it can be shown.
export function eventFilterToQuery(filter: MetricFilter): string | undefined {
  const rendered = renderNode(filter);
  if (rendered === null) {
    return undefined;
  }

  // Re-emit the scope-only `context` token(s) so the bar rehydrates the kind selection; ANDed at the
  // front and dropped again on the next parse, so the round-trip stays stable. The `context` field is
  // single-value (one kind per token — see MetricEventQueryBuilder's field def), so emit a separate
  // `context:=:<kind>` token per kind rather than one comma-joined token the single-select value cell
  // couldn't edit without dropping the other kinds.
  const contextKinds = contextKindsFromFilter(filter);
  if (contextKinds.length > 0) {
    const contextTokens = contextKinds.map((kind) => `context:=:${quoteIfNeeded(kind)}`).join(' AND ');
    return `${contextTokens} AND ${rendered}`;
  }

  return rendered;
}

// True if the bar can show this filter without losing anything: event-property and context-attribute
// leaves using a supported operator. Other operators can't be shown, so callers fall back to legacy.
export function isRepresentable(filter?: MetricFilter): boolean {
  if (!filter) {
    return true;
  }
  if (filter.type === 'group') {
    return filter.values.every(isRepresentable);
  }
  // Event-property and context-attribute leaves are both representable.
  if ((filter.type !== 'eventProperty' && filter.type !== 'contextAttribute') || !(filter.op in OP_TO_UI)) {
    return false;
  }
  // A single-value op is only representable with exactly one value and no negation; otherwise the
  // whole metric opens in the legacy editor rather than the bar silently dropping the clause.
  if (SINGLE_VALUE_OPS.has(filter.op)) {
    return (filter.values ?? []).length === 1 && !filter.negate;
  }
  return true;
}

// Which of the two bars a leaf belongs to: contextAttribute leaves go to the context bar, everything
// else (eventProperty) to the event bar. Keeps the category rule in one place for the split walks.
function leafCategory(leaf: ContextAttributeFilter | EventPropertyFilter): 'context' | 'event' {
  return leaf.type === 'contextAttribute' ? 'context' : 'event';
}

// Rebuilds one side of a split group: drop the pruned-away children, collapse a single child out of
// its wrapper group, and return undefined when nothing survives.
function pruneGroup(op: GroupFilter['op'], children: Array<MetricFilter | undefined>): MetricFilter | undefined {
  const surviving = children.filter((child): child is MetricFilter => child !== undefined);
  if (surviving.length === 0) {
    return undefined;
  }
  if (surviving.length === 1) {
    return surviving[0];
  }
  return { type: 'group', op, values: surviving, negate: false } satisfies GroupFilter;
}

// Splits a stored filter into its context-attribute and event-property halves, one per bar. Each side
// keeps the original group's op and structure with the other category's leaves pruned away.
export function splitFilterByCategory(filter?: MetricFilter): {
  contextFilter?: MetricFilter;
  eventFilter?: MetricFilter;
} {
  if (!filter) {
    return { contextFilter: undefined, eventFilter: undefined };
  }

  if (filter.type !== 'group') {
    return leafCategory(filter) === 'context'
      ? { contextFilter: filter, eventFilter: undefined }
      : { contextFilter: undefined, eventFilter: filter };
  }

  const contextChildren: Array<MetricFilter | undefined> = [];
  const eventChildren: Array<MetricFilter | undefined> = [];
  for (const child of filter.values) {
    const { contextFilter, eventFilter } = splitFilterByCategory(child);
    contextChildren.push(contextFilter);
    eventChildren.push(eventFilter);
  }

  return {
    contextFilter: pruneGroup(filter.op, contextChildren),
    eventFilter: pruneGroup(filter.op, eventChildren),
  };
}

// Recombines the two bars' filters into one stored filter, the inverse of splitFilterByCategory.
export function combineEventFilters(context?: MetricFilter, event?: MetricFilter): MetricFilter | undefined {
  if (!context && !event) {
    return undefined;
  }
  if (!context) {
    return event;
  }
  if (!event) {
    return context;
  }
  // Exact round-trip holds for single-category filters and a top-level AND of one context + one event
  // branch; deeper mixed trees are excluded upstream by isSplittableAcrossBars.
  return { type: 'group', op: 'and', values: [context, event], negate: false } satisfies GroupFilter;
}

// The leaf categories present anywhere in the subtree, used to detect cross-category groups.
function containedCategories(filter?: MetricFilter): Set<'context' | 'event'> {
  if (!filter) {
    return new Set();
  }
  if (filter.type !== 'group') {
    return new Set([leafCategory(filter)]);
  }
  const categories = new Set<'context' | 'event'>();
  for (const child of filter.values) {
    for (const category of containedCategories(child)) {
      categories.add(category);
    }
  }
  return categories;
}

// True if splitting the filter across the two bars preserves its meaning. Splitting re-ANDs the two
// halves, so a group that mixes categories under an OR would change meaning and is rejected.
export function isSplittableAcrossBars(filter?: MetricFilter): boolean {
  if (!filter || filter.type !== 'group') {
    return true;
  }
  if (filter.op === 'or' && containedCategories(filter).size > 1) {
    return false;
  }
  return filter.values.every(isSplittableAcrossBars);
}

// Gatekeeper for QueryBuilder vs legacy editor: the filter must be representable in the bar and safe
// to split across the two bars.
export function canEditWithQueryBuilder(filter: MetricFilter | undefined): boolean {
  return isRepresentable(filter) && isSplittableAcrossBars(filter);
}

// Context kinds chosen in the `context` scope token(s), first-seen and deduped (reads top-level and
// nested EXPRESSION tokens). Drives context-attribute scoping and conflict detection.
export function selectedContextKindsFromTokens(tokens: ParsedToken[]): string[] {
  const kinds: string[] = [];
  const seen = new Set<string>();
  const walk = (list: ParsedToken[]) => {
    for (const token of list) {
      if (token.type === 'FILTER' && token.field === CONTEXT_KIND_FIELD) {
        const values = Array.isArray(token.value) ? token.value : [token.value];
        for (const value of values) {
          const kind = String(value);
          // Skip the empty value that exists after the `context` token is added but before a kind
          // is chosen — it isn't a real pin and would trigger a redundant unscoped fetch.
          if (kind && !seen.has(kind)) {
            seen.add(kind);
            kinds.push(kind);
          }
        }
      } else if (token.type === 'EXPRESSION') {
        walk(token.inner);
      }
    }
  };
  walk(tokens);
  return kinds;
}

// Context kinds referenced by the context bar's filter tokens. Every non-`context` filter in this bar
// is a context attribute; its kind is the first segment after the optional `context_attributes_json.`
// prefix — so both picked (`context_attributes_json.<kind>.<attr>`) and custom-typed (`<kind>.<attr>`)
// filters are covered. Reads top-level and nested EXPRESSION tokens.
function contextAttributeKindsFromTokens(tokens: ParsedToken[]): string[] {
  const kinds: string[] = [];
  const walk = (list: ParsedToken[]) => {
    for (const token of list) {
      if (token.type === 'FILTER' && token.field !== CONTEXT_KIND_FIELD) {
        const rest = token.field.startsWith(CONTEXT_ATTRS_PREFIX)
          ? token.field.slice(CONTEXT_ATTRS_PREFIX.length)
          : token.field;
        const kind = rest.split('.')[0];
        if (kind) {
          kinds.push(kind);
        }
      } else if (token.type === 'EXPRESSION') {
        walk(token.inner);
      }
    }
  };
  walk(tokens);
  return kinds;
}

// True when a context kind is pinned via the `context` picker AND a context-attribute filter targets a
// different kind — e.g. pinning `account` while a `context_attributes_json.user.name` filter is set
// (whether the filter predates the pin or was typed as custom text). Such a filter can't match the
// pinned scope, so the context bar surfaces it as invalid and the form can't be saved.
export function hasContextKindConflict(tokens: ParsedToken[]): boolean {
  const pinnedKinds = selectedContextKindsFromTokens(tokens);
  if (pinnedKinds.length === 0) {
    return false;
  }
  const pinned = new Set(pinnedKinds);
  return contextAttributeKindsFromTokens(tokens).some((kind) => !pinned.has(kind));
}
